import React, { Fragment, useContext } from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { Route } from 'react-router-dom';

import { Hoverable, Icon, Text } from '../uiold';
import { themeColors } from '../uiold/Constants';
import { User } from '../../types/shared/User';
import { Account } from '../../types/shared/Account';

import { RouteDescriptor } from './types';
import { RouterContext } from './GlobalRouter';
import { ChangeRouteFn } from './BlockTransition';
import { getPath, shouldDisplayRoute, useIsActiveRoute } from './helpers';

type NavigationItemProps = {
  user: User | null | undefined;
  account: Account;
  route: RouteDescriptor;
};

export function NavigationItem(props: NavigationItemProps) {
  let { user, account, route } = props;
  let { gotoRoute } = useContext(RouterContext);
  let isActiveRoute = useIsActiveRoute();
  let isActive = isActiveRoute(route);

  if (!shouldDisplayRoute(user, account, route)) {
    return null;
  }

  if (route.type === 'page') {
    return (
      <NavigationLink
        route={route}
        isActive={isActive}
        collapsible={false}
        onChangeRoute={gotoRoute}
      />
    );
  }

  let subroutes = route.routes.filter((route) =>
    shouldDisplayRoute(user, account, route),
  );

  // Don't render a parent with no visible children
  if (subroutes.length === 0) {
    return null;
  }

  return (
    <Fragment>
      <NavigationLink
        route={route}
        isActive={isActive}
        collapsible={true}
        onChangeRoute={gotoRoute}
      />
      <View style={[styles.subroutes, isActive && styles.expandedSubroutes]}>
        {subroutes.map((route, i) => (
          <Route
            key={i}
            render={() => (
              <NavigationLink
                route={route}
                isActive={isActiveRoute(route)}
                collapsible={false}
                onChangeRoute={gotoRoute}
              />
            )}
          />
        ))}
      </View>
    </Fragment>
  );
}

type NavigationLinkProps = {
  route: RouteDescriptor;
  isActive: boolean;
  collapsible: boolean;
  onChangeRoute: ChangeRouteFn;
};

function NavigationLink(props: NavigationLinkProps) {
  let { isActive, collapsible, route, onChangeRoute } = props;
  let { name, icon } = route;
  let path = getPath(route);
  return (
    <TouchableOpacity
      href={path}
      style={styles.outlineNone}
      onPress={(event) => {
        event.preventDefault();
        onChangeRoute(path);
      }}
      activeOpacity={0.6}
    >
      <Hoverable
        render={(isHovered, eventHandlers) => (
          <View
            {...eventHandlers}
            style={[
              styles.navItem,
              isActive && !collapsible && styles.navItemActive,
            ]}
          >
            {icon && (
              <Icon
                name={icon}
                size="M"
                color={
                  isActive || isHovered
                    ? themeColors.blue
                    : themeColors.mainNavText
                }
              />
            )}
            <Text
              style={[
                styles.navItemText,
                (isActive || isHovered) && styles.navItemTextHovered,
              ]}
            >
              {name.toUpperCase()}
            </Text>
            {collapsible && (
              <Icon
                name="chevronDown"
                size="XS"
                color={
                  isActive || isHovered
                    ? themeColors.blue
                    : themeColors.mainNavText
                }
                style={[styles.arrow, isActive && styles.arrowUp]}
              />
            )}
          </View>
        )}
      />
    </TouchableOpacity>
  );
}

let transition = {
  transitionDuration: '0.25s',
  transitionTimingFunction: 'ease-out',
};

let styles = StyleSheet.create({
  subroutes: {
    transformOrigin: 'top left',
    maxHeight: 0,
    transform: [{ scaleY: 0 }],
    backgroundColor: themeColors.mainNavSubRouteBackground,
    overflow: 'hidden',
    transitionProperty: 'transform',
    transitionDuration: '0.25s',
    transitionTimingFunction: 'ease-out',
  },
  expandedSubroutes: {
    maxHeight: 'none',
    // TODO: We could probably do a better animated transform here.
    transform: [{ scaleY: 1 }],
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 20,
    height: 45,
    borderLeftWidth: 6,
    borderColor: themeColors.transparent,
    transitionProperty: 'border-color',
    ...transition,
  },
  navItemActive: {
    borderColor: themeColors.blue,
  },
  arrow: {
    position: 'absolute',
    right: 20,
    transitionProperty: 'transform',
    ...transition,
  },
  arrowUp: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    transform: 'rotate(-0.5turn)' as any,
  },
  navItemText: {
    position: 'absolute',
    left: 64,
    fontWeight: 'bold',
    fontSize: 11,
    color: themeColors.mainNavText,
    transitionProperty: 'color',
    ...transition,
  },
  navItemTextHovered: {
    color: themeColors.white,
  },
  outlineNone: {
    outlineWidth: 0,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any,
});
